import { EVENTS } from '@va/constants';
import { VisitorSegment, useGetVisitorSegments } from '@va/dashboard/feature/visitor-segments';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import { useReportBlockConfig } from '@va/ui/components/report-block';
import { LocalStorage, storeSegmentIds } from '@va/util/helpers';
import {
  EventBus,
  StorageKeysManager,
  useCustomizationContext,
  useEventsContext,
  usePermissionsProvider,
} from '@va/util/misc';
import { isArray } from 'lodash';
import { FC, PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type SegmentsContextType = {
  segments: Array<VisitorSegment>;
  isLoading: boolean;
  selectedSegments: Array<VisitorSegment>;
  isSegmentSelected: (segmentId: string) => boolean;
  toggleSegmentSelection: (id: string) => void;
  isExpanded: boolean;
  setIsExpanded: (v: boolean) => void;
  getSegmentName: (segmentId: string) => string | undefined;
};

export const SegmentsContext = createContext<SegmentsContextType>({
  selectedSegments: [] as VisitorSegment[],
} as SegmentsContextType);

const SegmentsContextProvider: FC<
  PropsWithChildren<{
    reportBlockId: string;
  }>
> = ({ children, reportBlockId }) => {
  const { getCustomValue } = useCustomizationContext();
  const { canEditSegments } = usePermissionsProvider();
  const { storageNamespace } = useReportBlockConfig();

  const sessionStoragePersistenceActive = useMemo(
    () => getCustomValue('sessionStoragePersistenceForSegments', true),
    [getCustomValue],
  );

  const getInitialSegmentIds = useCallback(() => {
    const customInitialSegmentIds = getCustomValue('initialSelectedSegmentsIds', undefined);
    if (customInitialSegmentIds) return customInitialSegmentIds;

    const storedSegmentIds = LocalStorage.getItem(
      StorageKeysManager.getReportSegmentsKey({ namespace: storageNamespace, reportBlockId }),
    );

    if (!storedSegmentIds || storedSegmentIds === 'null') return;

    try {
      const parsedIds = JSON.parse(storedSegmentIds) as string[];
      if (!isArray(parsedIds)) {
        throw new Error('invalid ids');
      }

      parsedIds.forEach((item) => {
        if (typeof item != 'string') {
          throw new Error('invalid ids');
        }
      });

      return parsedIds;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }, [getCustomValue, reportBlockId, storageNamespace]);

  const [selectedSegmentsIds, setSelectedSegmentsIds] = useState<string[]>(getInitialSegmentIds() ?? []);

  const translate = useTranslate();

  const { showErrorNotification } = useAddNotification();

  const [isExpanded, setIsExpanded] = useState<boolean>(getCustomValue('initialSegmentsExpanded', false));
  const { data: segments = [], isLoading } = useGetVisitorSegments();

  const { localEventBus } = useEventsContext();

  const isSegmentSelected = useCallback(
    (segmentId: string) => selectedSegmentsIds.includes(segmentId),
    [selectedSegmentsIds],
  );

  const selectedSegments = useMemo(
    () => segments.filter((item) => selectedSegmentsIds.includes(item.id)),
    [segments, selectedSegmentsIds],
  );

  const sendSegmentsChangedEvent = useCallback(
    (newIds: string[]) => {
      localEventBus?.dispatch(EVENTS.selectedSegmentsChanged, newIds);
      if (sessionStoragePersistenceActive) {
        storeSegmentIds(storageNamespace, reportBlockId, newIds);
      }
    },
    [localEventBus, reportBlockId, sessionStoragePersistenceActive, storageNamespace],
  );

  useEffect(() => {
    const l = EventBus.addListener(EVENTS.collapseSegments, () => {
      setIsExpanded(false);
    });

    return () => {
      l.removeListener();
    };
  }, []);

  const toggleSegmentSelection = useCallback(
    (id: string) => {
      if (!canEditSegments) {
        showErrorNotification(
          false,
          getCustomValue('disabledSegmentsMessage', translate('all.defaultWarnnings.cantEditSegments')),
        );
        return;
      }

      setSelectedSegmentsIds((prev) => {
        let next = [];

        if (isSegmentSelected(id)) {
          next = prev.filter((itemId) => itemId !== id);
          if (next.length === 0) {
            setIsExpanded(false);
          }
        } else {
          next = [...prev, id];
        }
        sendSegmentsChangedEvent(next);
        return next;
      });
    },
    [canEditSegments, getCustomValue, isSegmentSelected, sendSegmentsChangedEvent, showErrorNotification, translate],
  );

  const getSegmentName = useCallback(
    (segmentId: string) => {
      return selectedSegments.find((s) => s.id === segmentId)?.name;
    },
    [selectedSegments],
  );

  return (
    <SegmentsContext.Provider
      value={{
        segments: segments ?? [],
        isLoading,
        selectedSegments,
        isSegmentSelected,
        toggleSegmentSelection,
        isExpanded,
        setIsExpanded,
        getSegmentName,
      }}
    >
      {children}
    </SegmentsContext.Provider>
  );
};

function useSegmentsContext() {
  return useContext(SegmentsContext) as SegmentsContextType;
}

export { SegmentsContextProvider, useSegmentsContext };
